import React from 'react'
import classnames from 'classnames'
import kebabCase from 'lodash.kebabcase'

import {
  AnalyticsEvent,
  triggerGoogleTagManagerCustomEvent,
  googleTagManagerCustomEvents,
} from 'marketing-site/src/library/utils'
import { Icon } from 'marketing-site/src/mvp/library/elements/Icon'
import { Text } from 'marketing-site/src/library/elements/Text'
import { LoadingAnimation } from 'marketing-site/src/library/elements/LoadingAnimation'
import { CTATheme } from 'marketing-site/src/library/utils'
import { IProps } from './index'

export const CTAButton: React.FC<IProps> = ({
  variant = CTATheme.BlueFill,
  text,
  type = 'button',
  icon,
  small,
  dataAttribute,
  eventContext,
  loading,
  wide = false,
  disabled = false,
  children,
  onClick,
}) => {
  const dataAttributeObject = dataAttribute ? { [dataAttribute]: 'true' } : {}
  const analyticsEvent = new AnalyticsEvent({
    action: 'clicked',
    object: 'cta_button',
    context: eventContext || text.toLowerCase().split(' ').join('_'),
  })

  const handleClick = function (e: React.MouseEvent) {
    analyticsEvent.setPlaceFromPath(window.location.pathname)
    analyticsEvent.send()
    triggerGoogleTagManagerCustomEvent(googleTagManagerCustomEvents.ctaButtonClicked)
    return onClick(e)
  }

  const isValidChildren = React.isValidElement(children)

  const renderIcon = () => {
    switch (icon) {
      case 'Arrow':
        return variant.includes('LinkOnly') ? <Icon icon="arrow-link" /> : <Icon icon="arrow-cta" />
      case 'Play':
        return <Icon icon="circle-play-cta" />
      default:
        null
    }
  }

  const classes = classnames('cta-button', {
    [`cta-button--${kebabCase(variant)}`]: variant,
    'cta-button--small': small,
    'cta-button--unstyled': isValidChildren,
    'cta-button--is-loading': loading,
    'cta-button--wide': wide,
  })

  return (
    <>
      <button
        type={type}
        className={classes}
        data-testid="cta-button"
        {...dataAttributeObject}
        onClick={handleClick}
        disabled={loading || disabled}
      >
        {isValidChildren ? (
          <>{children}</>
        ) : (
          <>
            {loading && (
              <div className="cta-button__loading-animation">
                <LoadingAnimation />
              </div>
            )}
            <Text size="button-bold">{text}</Text>
            {icon && renderIcon()}
          </>
        )}
      </button>

      <style jsx>{`
        .cta-button {
          position: relative;
          display: inline-flex;
          justify-content: center;
          align-items: center;
          gap: 0.5rem;
          transition: all 200ms;
          font-weight: 700;
          text-decoration: none;
          white-space: nowrap;
          cursor: pointer;

          :global(span) {
            color: inherit;
          }

          :global(.icon) {
            vertical-align: middle;
          }

          &__loading-animation {
            display: flex;
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
          }

          &:not([class*='link-only']):not(.cta-button--unstyled) {
            height: 3rem;
            padding: 0 1.5rem;
            border-radius: 2rem;
          }

          &[class*='link-only'] {
            white-space: initial;

            :global(span) {
              border-bottom: ${!!icon ? 'none' : '1px solid currentColor'};
            }
          }

          &:hover:not([disabled]) {
            text-decoration: none;

            :global(.icon--arrow-cta),
            :global(.icon--arrow-link) {
              animation: arrow 0.4s ease-in-out forwards;
            }
          }

          &:disabled {
            pointer-events: none;

            &:not(.cta-button--is-loading) {
              opacity: 0.5;
            }
          }

          &:focus {
            outline: 0;
          }

          &.cta-button--unstyled {
            display: inline-block;
            border-radius: none;
            padding: 0;
            -webkit-text-decoration: none;
            text-decoration: none;
            text-align: unset;
            transition: unset;
            cursor: pointer;
            background-color: unset;
            border: none;
            color: unset;
          }

          &--small:not([class*='link-only']):not(.cta-button--unstyled) {
            height: 2.5rem;
          }

          &--wide {
            width: 100%;
          }

          &--is-loading {
            :global(span),
            :global(.icon) {
              opacity: 0;
            }
          }

          &--slate-fill,
          &--blue-fill,
          &--paper-fill,
          &--slate-outline,
          &--blue-outline,
          &--paper-outline,
          &--slate-fill-transparent-hover {
            &:active:not([disabled]) {
              transform: translateY(4px);
            }
          }

          &--slate-fill {
            background-color: var(--mvp-slate);
            color: var(--paper);

            &:focus,
            &:hover:not([disabled]) {
              background-color: var(--neutral-800);
              color: var(--paper);
            }
          }

          &--slate-outline {
            background-color: transparent;
            box-shadow: inset 0 0 0 2px var(--mvp-slate);
            color: var(--mvp-slate);

            &:focus,
            &:hover:not([disabled]) {
              box-shadow: inset 0 0 0 3px var(--neutral-800);
              color: var(--mvp-slate);
            }
          }

          &--slate-fill-transparent-hover {
            background-color: var(--mvp-slate);
            box-shadow: inset 0 0 0 2px var(--mvp-slate);
            color: var(--paper);

            &:focus,
            &:hover:not([disabled]) {
              background-color: transparent;
              box-shadow: inset 0 0 0 3px darken($mvp-slate, 15%);
              color: var(--mvp-slate);
            }
          }

          &--link-only-slate {
            color: var(--mvp-slate);
            padding: 0;
            border-radius: 0;
          }

          &--blue-fill {
            background-color: var(--mvp-blue);
            color: var(--paper);

            &:focus,
            &:hover:not([disabled]) {
              background-color: darken($mvp-blue, 15%);
              color: var(--paper);
            }
          }

          &--blue-outline {
            background-color: transparent;
            box-shadow: inset 0 0 0 2px var(--mvp-blue);
            color: var(--mvp-blue);

            &:focus,
            &:hover:not([disabled]) {
              box-shadow: inset 0 0 0 3px darken($mvp-blue, 15%);
              color: darken($mvp-blue, 15%);
            }
          }

          &--link-only-blue {
            color: var(--mvp-blue);
            padding: 0;
            border-radius: 0;
          }

          &--paper-fill {
            background-color: var(--paper);
            color: var(--mvp-blue);

            &:focus,
            &:hover:not([disabled]) {
              background-color: var(--mvp-stone);
            }
          }

          &--paper-outline {
            background-color: transparent;
            box-shadow: inset 0 0 0 2px var(--paper);
            color: var(--paper);

            &:focus,
            &:hover:not([disabled]) {
              background-color: transparent;
              box-shadow: inset 0 0 0 3px var(--mvp-stone);
              color: var(--mvp-stone);
            }
          }

          &--link-only-paper {
            color: var(--paper);
            padding: 0;
            border-radius: 0;
          }

          :global(.icon--arrow-cta),
          :global(.icon--arrow-link) {
            transform: translate3d(0, 0, 0);
            animation: arrow-reverse 0.4s ease-in-out;
          }

          @keyframes arrow {
            0% {
              transform: translate3d(0, 0, 0);
            }
            40% {
              transform: translate3d(5px, 0, 0);
            }
            60% {
              transform: translate3d(3px, 0, 0);
            }
            100% {
              transform: translate3d(4px, 0, 0);
            }
          }

          @keyframes arrow-reverse {
            0% {
              transform: translate3d(4px, 0, 0);
            }
            40% {
              transform: translate3d(-1px, 0, 0);
            }
            60% {
              transform: translate3d(1px, 0, 0);
            }
            100% {
              transform: translate3d(0, 0, 0);
            }
          }
        }
      `}</style>
    </>
  )
}
